import { useEffect, useMemo, useState } from 'react';
import locationsData from '@/data/locations.json';

export interface Location {
  id: string;
  name: string;
  type: 'city' | 'country';
  displayName: string;
  searchString: string;
  country?: string;
  /** Alternative names, on curated regions only. Not used by the web search. */
  aliases?: string[];
}

/**
 * Which search the suggestions are for: the liveaboard search offers where the
 * boats sail (the Bahamas, Brothers, Tubbataha), the course search where the
 * dive centers are.
 */
export type LocationScope = "courses" | "liveaboards";

/** A liveaboard quick pick, ranked by the backend on how many boats sail there. */
export interface PopularDestination {
  name: string;
  subtitle: string;
  value: string;
  boats: number;
}

/** The build-time file, kept as the offline and fallback copy. */
const BUNDLED_LOCATIONS = locationsData as Location[];

const FEATURED_LOCATION_IDS = new Set<string>([
  // Regions
  "region-bali-indonesia",
  "region-amed-bali-indonesia",
  "region-red-sea-egypt",
  "region-riviera-maya-mexico",
  "region-canary-islands-spain",
  "city-tenerife-spain",
  "city-lanzarote-spain",
  "city-fuerteventura-spain",
  "region-palawan-philippines",
  "region-koh-samui-thailand",
  "region-krabi-thailand",
  "city-krabi-thailand",
  "city-koh-phi-phi-thailand",
  "region-lombok-indonesia",
  "city-gili-trawangan-indonesia",
  "city-gili-gede-indonesia",
  "region-komodo-indonesia",
  "region-maldives",
  "region-sipadan-malaysia",
  "region-great-barrier-reef-australia",
  "region-florida-keys-united-states",
  "region-san-andres-colombia",
  "region-cyprus",
  "region-greece",
  // Key dive hubs
  "city-koh-tao-thailand",
  "city-surat-thani-thailand", // Province where Koh Tao is located
  "city-nusa-penida-indonesia",
  // Maldives dive hubs
  "city-fuvahmulah-maldives",
  "city-dhigurah-maldives",
  "city-rasdhoo-maldives",
]);

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** Match whole words/phrases so "krabi" does not match inside unrelated keys. */
const aliasTermMatches = (term: string, key: string): boolean => {
  if (term === key) return true;
  if (term.includes(key)) {
    return new RegExp(`(^|\\b)${escapeRegExp(key)}(\\b|$)`).test(term);
  }
  if (term.length >= 3 && key.includes(term)) {
    return new RegExp(`(^|\\b)${escapeRegExp(term)}(\\b|$)`).test(key);
  }
  return false;
};

const useLocations = (scope: LocationScope = "courses") => {
  /**
   * Starts from the file bundled at build time, then replaces it with what the
   * backend serves, so a dive center added in a new city is searchable without
   * a deploy. The search is usable from the first render and a failed request
   * changes nothing: leaving a diver with an empty search box is worse than
   * showing a list that is a few destinations short.
   */
  // The bundled file only knows dive center destinations, so the liveaboard
  // list starts empty rather than offering places no boat sails to.
  const [locations, setLocations] = useState<Location[]>(
    scope === "liveaboards" ? [] : BUNDLED_LOCATIONS
  );
  // Only the liveaboard answer carries quick picks.
  const [popular, setPopular] = useState<PopularDestination[]>([]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const response = await fetch(
          scope === 'liveaboards' ? '/api/locations?for=liveaboards' : '/api/locations'
        );
        if (!response.ok) return;
        const data = await response.json();
        if (!cancelled && Array.isArray(data?.locations) && data.locations.length > 0) {
          setLocations(data.locations as Location[]);
        }
        if (!cancelled && Array.isArray(data?.popular)) {
          setPopular(data.popular as PopularDestination[]);
        }
      } catch {
        // Keep the bundled list.
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [scope]);

  const searchLocations = useMemo(() => {
    return (query: string, limit: number = 10): Location[] => {
      if (!query || query.trim().length < 2) {
        return [];
      }

      const searchTerm = query.toLowerCase().trim();
      
      // Location aliases for autocomplete (e.g., "koh tao" should also show "Surat Thani")
      const locationAliases: Record<string, string[]> = {
        krabi: ["ao nang", "railay"],
        "koh phi phi": ["phi phi"],
        "phi phi": ["koh phi phi"],
        "koh tao": ["surat thani", "ko tao"],
        "ko tao": ["surat thani", "koh tao"],
        "surat thani": ["koh tao", "ko tao"],
        "canary islands": ["canarias", "tenerife", "lanzarote", "fuerteventura"],
        canarias: ["canary islands", "tenerife", "lanzarote", "fuerteventura"],
        tenerife: ["canary islands", "canarias"],
        lanzarote: ["canary islands", "canarias"],
        fuerteventura: ["canary islands", "canarias"],
        // Typing "gili" should surface both Gili Trawangan and Gili Gede in suggestions
        gili: ["gili trawangan", "gili gede"],
        "gili trawangan": ["gili gede", "gili"],
        "gili gede": ["gili trawangan", "gili"],
        // Maldives dive hubs — typing "maldives" should also surface the local islands
        maldives: ["fuvahmulah", "dhigurah", "rasdhoo"],
        fuvahmulah: ["maldives"],
        dhigurah: ["maldives", "south ari atoll"],
        rasdhoo: ["maldives", "ari atoll"],
        "ari atoll": ["dhigurah", "rasdhoo"],
      };

      // Check if search term matches any alias
      let additionalSearchTerms = [searchTerm];
      Object.entries(locationAliases).forEach(([key, aliases]) => {
        if (aliasTermMatches(searchTerm, key)) {
          additionalSearchTerms = Array.from(
            new Set([...additionalSearchTerms, ...aliases])
          );
        }
      });

      const filtered = locations.filter((location) => {
        // Check if location matches any of the search terms (including aliases)
        const locationNameLower = location.name.toLowerCase();
        const locationSearchLower = location.searchString.toLowerCase();

        const matches = additionalSearchTerms.some((term) =>
          locationSearchLower.includes(term) ||
          locationNameLower.includes(term)
        );

        if (!matches) return false;

        // Deprioritize non-featured cities (they stay in but come after featured/countries)
        return true;
      });

      // Ordenar: países primero, luego featured, luego resto; dentro de cada grupo por relevancia
      const sorted = filtered.sort((a, b) => {
        const aIsCountry = a.type === 'country';
        const bIsCountry = b.type === 'country';
        const aIsFeatured = FEATURED_LOCATION_IDS.has(a.id);
        const bIsFeatured = FEATURED_LOCATION_IDS.has(b.id);

        const aRank = aIsCountry ? 0 : aIsFeatured ? 1 : 2;
        const bRank = bIsCountry ? 0 : bIsFeatured ? 1 : 2;
        if (aRank !== bRank) return aRank - bRank;

        const aStartsWith = a.name.toLowerCase().startsWith(searchTerm);
        const bStartsWith = b.name.toLowerCase().startsWith(searchTerm);
        if (aStartsWith && !bStartsWith) return -1;
        if (!aStartsWith && bStartsWith) return 1;

        return a.name.localeCompare(b.name);
      });

      return sorted.slice(0, limit);
    };
  }, [locations]);

  const getLocationById = useMemo(() => {
    return (id: string): Location | undefined => {
      return locations.find(location => location.id === id);
    };
  }, [locations]);

  const getLocationByName = useMemo(() => {
    return (name: string): Location | undefined => {
      return locations.find(location => 
        location.name.toLowerCase() === name.toLowerCase()
      );
    };
  }, [locations]);

  return {
    locations,
    popular,
    searchLocations,
    getLocationById,
    getLocationByName,
  };
};

export default useLocations;